import { useSeo } from '../lib/seo';
/**
 * /accessibility — UK Public-Sector-Bodies-Accessibility-Regulations style
 * statement page. This is Phase 1 of the wider accessibility roadmap:
 *
 *   1. (this file)  Tell visitors what we've done and how to contact us.
 *   2. Phase 2      Venue-level accessibility schema + admin self-declaration
 *   3. Phase 3      Bulk ingest from Google Places + Changing Places + OSM
 *   4. Phase 4      /explore filters + Plan-My-Day step-free toggle
 *   5. Later        AccessAble deep-links, editorial /accessible-days page
 *
 * Tone: honest. We list known limitations rather than pretending the site
 * is perfect, because disabled visitors are used to overpromised sites.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

const LAST_REVIEWED = '18 February 2026';

export default function AccessibilityPage() {
  useSeo(
    'Accessibility Statement | Go Leicestershire',
    'Our commitment to an accessible website and accessible days out across Leicestershire.'
  );
  return (
    <div data-testid="accessibility-page" className="bg-base">
      <Helmet>
        <title>Accessibility · GoLeicestershire</title>
        <meta
          name="description"
          content="GoLeicestershire's accessibility statement — what we've done to make the site usable by everyone, known limitations, and how to report problems."
        />
        <meta name="robots" content="index,follow" />
      </Helmet>

      <section className="bg-stone border-b border-charcoal/10">
        <div className="max-w-[860px] mx-auto px-6 lg:px-12 py-16 md:py-20">
          <div className="label text-heritage mb-3">Our commitment</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6">
            Accessibility on GoLeicestershire
          </h1>
          <p className="text-lg md:text-xl text-charcoal/75 leading-relaxed">
            We want this site to work for every visitor — including people who use screen readers,
            keyboards instead of mice, large text, voice control or have other access needs.
            This page sets out what we&apos;ve done, where we still fall short, and how to tell
            us if something isn&apos;t working for you.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-charcoal/55">
            <Clock size={14} /> Last reviewed: {LAST_REVIEWED}
          </div>
        </div>
      </section>

      <section className="max-w-[860px] mx-auto px-6 lg:px-12 py-16 md:py-20 prose prose-lg max-w-none [&_h2]:font-display [&_h2]:text-3xl [&_h2]:mt-12 [&_h2]:mb-4 [&_h3]:font-display [&_h3]:text-xl [&_h3]:mt-8 [&_h3]:mb-2 [&_p]:mb-5 [&_ul]:mb-5 [&_ul]:list-disc [&_ul]:list-inside [&_li]:mb-2 text-charcoal/85 leading-relaxed">
        <h2>How we&apos;ve built the site</h2>
        <p>
          GoLeicestershire is designed to meet the Web Content Accessibility Guidelines
          (<strong>WCAG 2.2 AA</strong>) wherever possible. In practice, this means:
        </p>
        <ul>
          <li>Every page has a skip-to-main-content link (try pressing Tab at the top of any page).</li>
          <li>The site is navigable end-to-end with the keyboard alone.</li>
          <li>Headings, lists, buttons and forms use the right HTML elements so assistive tech can interpret them correctly.</li>
          <li>Colour contrast meets the 4.5:1 minimum for body text.</li>
          <li>Animations and motion respect your OS-level &quot;Reduce motion&quot; setting.</li>
          <li>Images carry descriptive alt text where they convey information.</li>
          <li>Forms have visible labels and clear error messages.</li>
        </ul>

        <h2>Where we still fall short</h2>
        <p>
          We&apos;re honest about this because we&apos;d rather you know:
        </p>
        <ul>
          <li>
            <strong>Older attraction photos</strong> may have generic alt text. We&apos;re working
            through these to add proper descriptions.
          </li>
          <li>
            <strong>Some PDF documents</strong> embedded in editorial content (e.g. event flyers
            uploaded by venues) may not be fully accessible. If you need information from one,
            email us and we&apos;ll provide it in a different format.
          </li>
          <li>
            <strong>Detailed venue accessibility information</strong> (step-free access, hearing
            loops, Changing Places, sensory-friendly times) is currently shown only where venues
            have given us that information directly. We&apos;re building a richer accessibility
            layer over the coming weeks, drawing on multiple sources.
          </li>
        </ul>

        <h2>Tell us about a problem</h2>
        <p>
          If you find something that isn&apos;t working — a page that traps your screen reader,
          unreadable text, a button you can&apos;t reach with the keyboard, or a venue we&apos;ve
          described inaccurately — please tell us. We treat these reports as a priority.
        </p>
        <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
          <a
            href="mailto:accessibility@goleicestershire.com?subject=Accessibility%20report"
            data-testid="accessibility-email"
            className="group bg-white border border-charcoal/10 rounded-2xl p-5 hover:border-heritage/50 transition-colors block"
          >
            <Mail size={18} className="text-heritage mb-2" />
            <div className="font-semibold text-charcoal text-sm mb-1">Email us</div>
            <div className="text-charcoal/70 text-sm">accessibility@goleicestershire.com</div>
          </a>
          <a
            href="tel:+441162994444"
            data-testid="accessibility-phone"
            className="group bg-white border border-charcoal/10 rounded-2xl p-5 hover:border-heritage/50 transition-colors block"
          >
            <Phone size={18} className="text-heritage mb-2" />
            <div className="font-semibold text-charcoal text-sm mb-1">Call us</div>
            <div className="text-charcoal/70 text-sm">0116 299 4444 (Mon–Fri, 9am–5pm)</div>
          </a>
        </div>
        <p className="text-sm text-charcoal/65">
          We aim to acknowledge accessibility reports within <strong>two working days</strong>
          and to either fix the issue or explain a workaround within ten.
        </p>

        <h2>Accessibility of the venues we list</h2>
        <p>
          We list hundreds of venues across Leicester and Leicestershire, and each has its own
          access provision. Where a venue has told us about its facilities — step-free entry,
          accessible toilets, hearing loops, sensory-friendly sessions — we show that on the
          venue page, with the date the information was last confirmed.
        </p>
        <p>
          For the most detailed, photo-led access information about UK venues, we recommend the
          excellent guides published by{' '}
          <a
            href="https://www.accessable.co.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-heritage hover:underline"
            data-testid="accessibility-accessable-link"
          >
            AccessAble
          </a>{' '}
          and the user-written reviews on{' '}
          <a
            href="https://www.euansguide.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-heritage hover:underline"
            data-testid="accessibility-euans-link"
          >
            Euan&apos;s Guide
          </a>
          . Where these guides exist for a venue we feature, we&apos;ll link to them from
          the venue&apos;s page.
        </p>
        <div className="not-prose bg-mustard/15 border border-mustard/50 rounded-2xl p-4 my-6 flex gap-3 items-start">
          <AlertTriangle size={18} className="text-charcoal/80 shrink-0 mt-0.5" />
          <div className="text-sm text-charcoal/85">
            <strong>Please double-check critical details with the venue before you travel.</strong>{' '}
            Accessibility provision can change (a lift goes out of service, a quiet hour moves).
            For anything that affects whether you can have a good day out, a quick phone call to
            the venue is the safest belt-and-braces step. If a venue&apos;s information on our
            site turns out to be wrong, tell us — we&apos;ll fix it.
          </div>
        </div>

        <h2>Technical specification</h2>
        <p>
          This site relies on the following technologies for display, control and assistive
          technology compatibility: HTML, CSS, JavaScript (React). It is designed to be
          compatible with current versions of: Chrome, Edge, Firefox, Safari, and the
          screen readers built into Windows (Narrator), macOS (VoiceOver) and Android (TalkBack),
          plus NVDA and JAWS.
        </p>

        <h2>Enforcement and complaints</h2>
        <p>
          If you contact us about an accessibility problem and aren&apos;t satisfied with our
          response, you can escalate to the Equality and Human Rights Commission (EHRC). The
          EHRC is responsible for enforcing the Equality Act 2010.
        </p>

        <div className="not-prose mt-12 pt-6 border-t border-charcoal/10 flex items-start gap-3 text-sm text-charcoal/55">
          <CheckCircle2 size={16} className="text-heritage shrink-0 mt-0.5" />
          <div>
            This statement was prepared on {LAST_REVIEWED}. It will be reviewed at least every
            six months and whenever significant changes are made to the site.
          </div>
        </div>
      </section>
    </div>
  );
}
